import { nutritionPatch, TANK_REFILL_FIELDS } from './nutrition_utils';

const clone = (value) => JSON.parse(JSON.stringify(value));
const simulationSetpoints = (recipe) => [
  recipe?.ec_setpoint ?? 250,
  recipe?.ph_setpoint ?? 600,
  recipe?.temp_setpoint ?? 220
];

// Small, slow changes in protocol units, shared by tank summaries and sensor details.
export function simulationMeasurements(index, now = Date.now()) {
  const drift = Math.round(2 * Math.sin(now / 30000 + index));
  return [180 + index * 15 + drift, 610 + index * 5 + drift, 215 + drift, 320 + drift, 965 + drift];
}

export function createSimulationNutrientReadings(now) {
  return Array.from({ length: 4 }, (_, index) => ({
    measurement_configured_mask: 31,
    // A discovered pH sensor is disconnected; its retained raw value is not valid.
    measurement_valid_mask: index === 1 ? 29 : 31,
    measurements: simulationMeasurements(index, now)
  }));
}

// The legacy Info and sensor-detail DTOs use -1; Tank Info uses validity bits.
export const simulationNutrientValue = (reading, index) =>
  reading.measurement_valid_mask & (1 << index) ? reading.measurements[index] : -1;

function monitoring(sourceZone, index, readings) {
  const bound = sourceZone !== 255;
  return {
    level: !bound ? 5 : index === 1 ? 4 : 2,
    level_percent: 255,
    measurement_source_zone: sourceZone,
    measurement_source_index: bound ? index : 255,
    ...(bound
      ? readings[index]
      : {
          measurement_configured_mask: 0,
          measurement_valid_mask: 0,
          measurements: [0, 0, 0, 0, 0]
        })
  };
}

// Example controller configuration. Route IDs intentionally differ from valve indexes.
export function createSimulationTankScope(scope, zoned, recipes = [], sharedSource = 0, readings) {
  const ids = zoned ? (scope === 254 ? [7] : scope < 7 ? [scope] : []) : [0, 1];
  const tanks = ids.map((tank_id) => ({
    tank_id,
    setup_type: tank_id === 1 ? 0 : 1,
    selected_route_id: 0,
    ...(zoned && scope !== 254 && tank_id !== 1
      ? {
          refill_strategy: 1,
          fill_timeout_seconds: 1200,
          planned_offset_minutes: -60
        }
      : {}),
    low_allowance_minutes: 0,
    low_finish_reserve_minutes: 0,
    allowed_route_mask: tank_id === 1 ? 0 : 520
  }));
  const infoIds = zoned && scope !== 254 ? [...ids, 7] : ids;
  return {
    nutrition: {
      schema_version: 2,
      nutrient_settings: scope === 254 ? [] : recipes,
      tanks,
      routes: [
        { route_id: 3, name: 'Fresh water' },
        { route_id: 9, name: 'Nutrient supply' }
      ]
    },
    irrigation_tank_info: infoIds.map((tank_id) => ({
      tank_id,
      name: tank_id === 7 ? 'Shared tank' : `Tank ${tank_id + 1}`,
      enabled: true,
      availible_in_irrigation_zones: [0, 1, 2, 3],
      owner_zone: zoned && tank_id === 7 ? 254 : scope,
      state: 0,
      active_route_id: 0,
      fault: 0,
      low_grace_remaining_seconds: 0,
      low_allowance_remaining_seconds: 0,
      // Local mixing tanks export configured targets; shared storage has none.
      setpoints: tank_id === 7 ? [0, 0, 0] : simulationSetpoints(recipes[zoned ? 0 : tank_id]),
      setpoint_valid_mask: tank_id === 7 ? 0 : 7,
      ...monitoring(
        tank_id === 7 ? sharedSource : zoned ? scope : 0,
        tank_id === 7 ? 1 : zoned ? 0 : tank_id,
        readings
      )
    }))
  };
}

export function addSimulationTankScopes(response, readings) {
  const { data, is_zoned: zoned } = response;
  const sharedSource = zoned && data.zones.length ? Number(data.zones[0].id) : 255;
  if (zoned) {
    Object.assign(data, createSimulationTankScope(254, true, [], sharedSource, readings));
    data.zones.forEach((zone) =>
      Object.assign(
        zone.settings,
        createSimulationTankScope(
          Number(zone.id),
          true,
          zone.settings.nutrition?.nutrient_settings,
          sharedSource,
          readings
        )
      )
    );
  } else
    Object.assign(
      data,
      createSimulationTankScope(0, false, data.nutrition?.nutrient_settings, 255, readings)
    );
}

// Persist mock settings per controller and owner, independently of generated live values.
export function retainSimulationNutrition(simulation, response) {
  simulation.nutritionScopes ??= {};
  const scopes = response.is_zoned
    ? [[254, response.data], ...response.data.zones.map((zone) => [Number(zone.id), zone.settings])]
    : [[0, response.data]];
  scopes.forEach(([scope, settings]) => {
    const previous = simulation.nutritionScopes[scope];
    if (previous) {
      const recipes = settings.nutrition.nutrient_settings;
      settings.nutrition = clone(previous.nutrition);
      if (!settings.nutrition.nutrient_settings.length && recipes.length) {
        settings.nutrition.nutrient_settings = recipes;
      }
    }
    settings.irrigation_tank_info.forEach((info) => {
      const ownerNutrition =
        info.owner_zone === scope
          ? settings.nutrition
          : simulation.nutritionScopes[info.owner_zone]?.nutrition;
      const tank = ownerNutrition?.tanks.find((tank) => tank.tank_id === info.tank_id);
      const until = simulation.manualTankRefills?.[info.tank_id] ?? 0;
      info.state =
        until > Date.now() ? 4 : tank?.selected_route_id && tank.refill_strategy !== 0 ? 1 : 0;
      info.active_route_id = info.state === 4 ? tank.selected_route_id : 0;
      if (info.setpoint_valid_mask) {
        // Mirror effective accepted settings on each poll, including while idle.
        info.setpoints = simulationSetpoints(
          settings.nutrition.nutrient_settings[info.measurement_source_index]
        );
      }
    });
    simulation.nutritionScopes[scope] = clone({
      nutrition: settings.nutrition,
      irrigation_tank_info: settings.irrigation_tank_info
    });
  });
}

export function saveSimulationNutrition(simulation, data) {
  // WebsocketManager normalizes the GUI's zone field to the DTO's id field.
  const scope = simulation.zones.length ? Number(data.id ?? data.zone) : 0;
  const current = simulation.nutritionScopes?.[scope];
  const patch = data.nutrition;
  try {
    if (
      !current ||
      patch?.schema_version !== 2 ||
      (patch.tanks !== undefined && !Array.isArray(patch.tanks)) ||
      (patch.nutrient_settings !== undefined && !Array.isArray(patch.nutrient_settings))
    )
      throw new Error();
    const fields = {};
    const ids = new Set();
    (patch.tanks ?? []).forEach((tank) => {
      if (ids.has(tank.tank_id)) throw new Error();
      ids.add(tank.tank_id);
      TANK_REFILL_FIELDS.forEach((key) => {
        if (key in tank) fields['tanks.' + tank.tank_id + '.' + key] = tank[key];
      });
      fields[`tanks.${tank.tank_id}.selected_route_id`] = tank.selected_route_id;
      fields[`tanks.${tank.tank_id}.low_allowance_minutes`] = tank.low_allowance_minutes;
      fields[`tanks.${tank.tank_id}.low_finish_reserve_minutes`] = tank.low_finish_reserve_minutes;
    });
    nutritionPatch(current.nutrition, current.irrigation_tank_info, scope, fields);
    const recipes = patch.nutrient_settings ?? [];
    if (
      recipes.length > current.nutrition.nutrient_settings.length ||
      (scope === 254 && recipes.length)
    )
      throw new Error();
    const next = clone(current.nutrition);
    recipes.forEach((recipe, index) => {
      next.nutrient_settings[index] = clone(recipe);
    });
    (patch.tanks ?? []).forEach((tank) =>
      Object.assign(
        next.tanks.find((item) => item.tank_id === tank.tank_id),
        {
          selected_route_id: tank.selected_route_id,
          low_allowance_minutes: tank.low_allowance_minutes,
          low_finish_reserve_minutes: tank.low_finish_reserve_minutes,
          ...Object.fromEntries(
            TANK_REFILL_FIELDS.filter((key) => key in tank).map((key) => [key, tank[key]])
          )
        }
      )
    );
    current.nutrition = next;
    return { type: 'info', message_parts: ['Nutrition settings saved'], timestamp: Date.now() };
  } catch (_) {
    return {
      type: 'error',
      message_parts: ['Invalid Nutrition settings; nothing applied'],
      timestamp: Date.now()
    };
  }
}

// UI demonstration only; firmware flow/level/reboot scenarios run against SAM C.
export function simulationTankRefill(simulation, action) {
  const match = /^action_tank_refill_([0-7])$/.exec(action.id);
  const id = match ? Number(match[1]) : -1;
  const current = simulation.nutritionScopes?.[Number(action.zone_id)];
  const tank = current?.nutrition?.tanks.find((row) => row.tank_id === id);
  const info = current?.irrigation_tank_info?.find((row) => row.tank_id === id);
  let key = 'tank_action_unavailable';
  let accepted = false;
  if (
    simulation.model.type === 5155 &&
    tank?.setup_type === 1 &&
    info?.owner_zone === Number(action.zone_id) &&
    TANK_REFILL_FIELDS.every((field) => field in tank)
  ) {
    if (!Number.isInteger(action.value) || action.value < 0 || action.value > 65535)
      key = 'tank_action_invalid';
    else if (action.value > 0 && !tank.selected_route_id) key = 'tank_action_unavailable';
    else {
      simulation.manualTankRefills ??= {};
      if (action.value > 0 && (simulation.manualTankRefills[id] ?? 0) > Date.now())
        key = 'tank_action_busy';
      else {
        simulation.manualTankRefills[id] = Date.now() + action.value * 1000;
        key = action.value === 0 ? 'tank_action_stop_requested' : 'tank_action_queued';
        accepted = true;
      }
    }
  }
  return { type: accepted ? 'info' : 'error', message_parts: [key], timestamp: Date.now() };
}
